import { Label, SearchField } from '@heroui/react'

import { ConfirmDialog } from '@/components/ui/form-modal'
import { errorMessage } from '@/lib/api/api-error'

import type { PendingBranchRemoval } from '../hooks'
import { translated } from '../types'
import type { ProductSummary } from '../types'

/*
 * The pieces the Branches tab's two views — the grid and the single branch —
 * draw the same way.
 */

/** Picture, name and SKU — the same face the Products tab gives a row. */
export function BranchProductCell({ product }: { product: ProductSummary }) {
  const variants = product.children?.length ?? 0

  return (
    <span className="flex items-center gap-2">
      {product.thumbnail?.url ? (
        <img
          src={product.thumbnail.url}
          alt=""
          className="border-border size-10 shrink-0 rounded-lg border object-cover"
        />
      ) : (
        <span className="border-border bg-surface size-10 shrink-0 rounded-lg border" />
      )}

      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{translated(product.name)}</span>
        <span className="text-muted truncate text-xs">
          {product.sku || 'No SKU'}
          {variants > 0 && ` · ${variants} variants, sold together`}
        </span>
      </span>
    </span>
  )
}

export function BranchProductSearch({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <SearchField
      value={value}
      onChange={onChange}
      aria-label="Search products"
      className="flex flex-1 flex-col gap-1.5"
    >
      <Label className="text-muted text-xs">Search</Label>
      <SearchField.Group>
        <SearchField.SearchIcon />
        <SearchField.Input placeholder="Name or SKU" />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  )
}

export function BranchWriteError({ error }: { error: unknown }) {
  if (error == null) return null

  return (
    <p role="alert" className="text-danger text-xs">
      {errorMessage(error, 'Could not update where that product is sold. Please try again.')}
    </p>
  )
}

/** The "are you sure" before an unlink clears a branch's stock count — see `useBranchLinks`. */
export function BranchRemovalConfirm({
  removal,
  onConfirm,
  onCancel,
}: {
  removal: PendingBranchRemoval | null
  onConfirm: () => void
  onCancel: () => void
}) {
  const count = removal?.products.length ?? 0
  // Kept generic rather than blank while the dialog animates shut, when
  // `removal` has already been cleared.
  const subject =
    count === 0
      ? 'these products'
      : count === 1
        ? translated(removal?.products[0].name)
        : `${count} products`
  const branchName = removal?.branchName ?? 'this branch'

  return (
    <ConfirmDialog
      isOpen={removal !== null}
      onOpenChange={(isOpen) => !isOpen && onCancel()}
      title={`Stop selling ${subject} at ${branchName}?`}
      body={`The stock counted at ${branchName} is cleared. Adding ${
        count === 1 ? 'it' : 'them'
      } back later starts that branch from zero. Other branches keep theirs.`}
      confirmLabel="Stop selling"
      onConfirm={onConfirm}
      isBusy={false}
    />
  )
}
